/**
 * database.js
 * 
 * Functions for searching and applying defect database entries.
 * Supports bilingual search (German/English) and auto-fill functionality.
 * 
 * Exports:
 * - searchDefectsDatabase: Search database by query string
 * - getDefectSuggestionsByElement: Get suggestions for selected element
 * - applyDefectDatabaseToForm: Auto-fill form from selected defect
 */

/**
 * Search defects database based on user input
 * Supports both German and English simultaneously
 * 
 * @param {string} searchQuery - Search query string
 * @param {Object} filters - Optional filters (not currently used)
 * @param {Object} defectsData - Defects database object
 * @returns {Array} - Array of matching defects
 */
export const searchDefectsDatabase = (searchQuery = '', filters = {}, defectsData = null) => {
  if (!defectsData?.defects) {
    return [];
  }

  const query = searchQuery.toLowerCase().trim();
  
  if (!query) {
    return [];
  }

  const results = defectsData.defects.filter(defect => {
    // Search in both German and English content simultaneously
    const elementDe = defect.element?.de || '';
    const elementEn = defect.element?.en || '';
    const defectTypeDe = defect.defectType?.de || '';
    const defectTypeEn = defect.defectType?.en || '';
    const descriptionDe = defect.description?.de || '';
    const descriptionEn = defect.description?.en || '';
    const searchTermsDe = defect.searchTerms?.de || [];
    const searchTermsEn = defect.searchTerms?.en || [];

    // Check if query matches any field in either language
    const matchesElement = elementDe.toLowerCase().includes(query) || elementEn.toLowerCase().includes(query);
    const matchesDefectType = defectTypeDe.toLowerCase().includes(query) || defectTypeEn.toLowerCase().includes(query);
    const matchesDescription = descriptionDe.toLowerCase().includes(query) || descriptionEn.toLowerCase().includes(query);
    const matchesSearchTerms = 
      searchTermsDe.some(term => term.toLowerCase().includes(query) || query.includes(term.toLowerCase())) ||
      searchTermsEn.some(term => term.toLowerCase().includes(query) || query.includes(term.toLowerCase()));

    return matchesElement || matchesDefectType || matchesDescription || matchesSearchTerms;
  });
  
  return results;
};

/**
 * Get defect suggestions based on selected element
 * 
 * @param {string} elementName - Selected element name
 * @param {Object} defectsData - Defects database
 * @param {string} projectLanguage - Project language (de/en)
 * @returns {Array} - Array of suggested defects (max 5)
 */
export const getDefectSuggestionsByElement = (elementName, defectsData, projectLanguage = 'de') => {
  if (!elementName || !defectsData) {
    return [];
  }

  // Extract the subcategory from hierarchical format "Category - Subcategory"
  let elementToMatch = elementName;
  if (elementName.includes(' - ')) {
    elementToMatch = elementName.split(' - ')[1] || elementName;
  }

  const elementLower = elementToMatch.toLowerCase();
  
  // Use defectsData directly (it's an array) or defectsData.defects if it's an object
  const defectsArray = Array.isArray(defectsData) ? defectsData : (defectsData.defects || []);
  
  // Direct matching without complex mappings - let the database search terms handle the complexity
  const matchedDefects = defectsArray.filter(defect => {
    // Handle both old format (defect.element.de) and new format (defect.element)
    const elementText = (defect.element?.de || defect.element || '').toLowerCase();
    const searchTerms = defect.searchTerms?.de || [];
    
    // Direct element text matching
    if (elementText.includes(elementLower)) {
      return true;
    }
    
    // Search terms matching
    if (searchTerms.some(term => term.toLowerCase().includes(elementLower) || elementLower.includes(term.toLowerCase()))) {
      return true;
    }
    
    return false;
  }).slice(0, 5); // Limit to top 5 suggestions
  
  return matchedDefects;
};

/**
 * Apply selected defect data to form fields with language-aware recommendations
 * 
 * @param {Object} defect - Selected defect from database
 * @param {Function} setFieldValue - Formik setFieldValue function
 * @param {string} projectLanguage - Project language (de/en)
 * @param {boolean} showBilingual - Whether to show bilingual text
 */
export const applyDefectDatabaseToForm = (defect, setFieldValue, projectLanguage = 'de', showBilingual = false) => {
  if (!defect || !setFieldValue) {
    return;
  }

  // Helper function to get text with language fallback
  const getText = (textObj) => textObj?.[projectLanguage] || textObj?.de || textObj?.en || '';

  // Set defect description
  setFieldValue('defectDescription', getText(defect.description));
  
  // Set repair recommendation as measure description
  let measureDesc = '';
  
  if (showBilingual && defect.repairRecommendation?.de && defect.repairRecommendation?.en) {
    // Bilingual recommendation
    measureDesc = projectLanguage === 'en' 
      ? `${defect.repairRecommendation.en}\n\n[DE] ${defect.repairRecommendation.de}`
      : `${defect.repairRecommendation.de}\n\n[EN] ${defect.repairRecommendation.en}`;
  } else {
    // Single language recommendation with fallback
    const repairRecommendation = getText(defect.repairRecommendation);
    
    if (repairRecommendation) {
      measureDesc = repairRecommendation;
    } else {
      // Create fallback recommendation
      const elementName = getText(defect.element);
      const defectTypeName = getText(defect.defectType);
      
      measureDesc = projectLanguage === 'en' 
        ? `Repair measures for ${elementName}: ${defectTypeName}`
        : `Reparaturmaßnahmen für ${elementName}: ${defectTypeName}`;
    }
  }
  
  setFieldValue('measureDescription', measureDesc);
  
  // Set cost information from Excel structure
  setFieldValue('pricePerUnit', defect.cost?.average || defect.cost?.basePrice || 0);
  
  // Extract unit properly - remove € prefix if present
  let unit = defect.cost?.unit || 'm²';
  if (unit.startsWith('€/')) {
    unit = unit.substring(2); // Remove "€/" prefix
  }
  setFieldValue('unit', unit);
  setFieldValue('quantity', 1);
  
  // Use formMappings for dropdown values (Excel-aligned)
  if (defect.formMappings) {
    // Set German values directly
    setFieldValue('riskType', defect.formMappings.riskType || 'mittel');
    setFieldValue('defectType', defect.formMappings.defectType || 'Defekt');
    setFieldValue('priorityType', defect.formMappings.priorityType || 'mittelfristig');
  }
};
